import { PureComponent, ReactNode, ReactElement } from 'react';
import { Table, TableProps } from 'antd';
import {
    SortableContainer as sortableContainer,
    SortableElement as sortableElement,
    SortableHandle,
    SortStart,
} from 'react-sortable-hoc';
import { MenuOutlined } from '@ant-design/icons';
import { IGNORE_CONFIG_KEYS } from '../../../../../constants';
import { randomString } from '../../../../../store/utils';
import { handleTableDragWidth } from '../../../../common/TableUtils';

export const DragHandle = SortableHandle(() => (
    <MenuOutlined style={{ cursor: 'move', color: '#999' }} onPointerLeaveCapture={undefined} />
));

export const SortableItem = sortableElement(
    (props: { children: ReactNode; className: string; 'data-row-key': string }): ReactElement => {
        return <tr {...props} />;
    },
);

export const SortableContainer = sortableContainer(
    (props: { children: ReactNode; className: string; id: string }): ReactElement => {
        return <tbody {...props} id={props.id} />;
    },
);

type Item = {
    id: string;
};

interface Props<Record extends Item> extends TableProps<Record> {
    children?: ReactNode;
    onSortEnd?: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void;
    onSortStart?: (params: { index: number }) => void;
    findIndexForRecordKey: (id: string) => number;
    className: string;
}

class TableWithDragableRows<Record extends Item> extends PureComponent<Props<Record>> {
    dragContainerId = randomString(4);

    onSortStart = (params: SortStart): void => {
        handleTableDragWidth(params, this.dragContainerId);

        this.props.onSortStart && this.props.onSortStart(params);
    };

    DraggableContainer = (props: { children: ReactNode; className: string }): JSX.Element => (
        <SortableContainer
            id={this.dragContainerId}
            useDragHandle
            onSortEnd={this.props.onSortEnd}
            onSortStart={this.onSortStart}
            helperContainer={() => document.getElementById(this.dragContainerId) || document.body}
            {...props}
        />
    );

    DraggableBodyRow = ({
        className,
        ...restProps
    }: {
        children: ReactNode;
        className: string;
        'data-row-key': string;
        style: React.StyleHTMLAttributes<HTMLDivElement>;
    }): JSX.Element => {
        const { findIndexForRecordKey: findIndexForRecordWithId } = this.props;
        const index = findIndexForRecordWithId(restProps['data-row-key']);
        const hideRow =
            this.props.dataSource && IGNORE_CONFIG_KEYS.has(this.props.dataSource[index]?.id || '');
        return hideRow ? (
            <div className="h-0"></div>
        ) : (
            <SortableItem className={'editable-row'} index={index} {...restProps} />
        );
    };

    components = {
        body: {
            wrapper: this.DraggableContainer,
            row: this.DraggableBodyRow,
        },
    };

    render(): ReactNode {
        const { children, className, rowClassName, ...restProps } = this.props;
        return (
            <Table
                tableLayout="fixed"
                components={this.components}
                className={className}
                rowClassName={rowClassName || (() => 'editable-row')}
                bordered
                rowKey={(record, index) => `${record.id}-${index}`}
                {...restProps}
            >
                {children}
            </Table>
        );
    }
}

export default TableWithDragableRows;
